import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Box, Button, Typography, TextField, IconButton, Snackbar, Alert } from "@mui/material";
import { FileCopy as CopyIcon } from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";
import useStyles from "./style/PaymentPopupStyle";
import useAuthStore from "store/authStore";
import waiting from ".././assets/images/waiting.png";
import { BASE_URL } from "service";

const PaymentPopup = ({ isSubscribed, verifiedStatus, onClose }) => {
  const { email } = useAuthStore();
  const [transactionId, setTransactionId] = useState(""); // State for transaction ID input
  ("h5");
  const [screenshot, setScreenshot] = useState(null); // State for uploaded screenshot file
  const [loading, setLoading] = useState(false); // Loading state for submit button
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // States for QR code and UPI ID from API
  const [qrImage, setQrImage] = useState("");
  const [apiUpiId, setApiUpiId] = useState("");

  // Fetch QR code and UPI ID from API
  useEffect(() => {
    const fetchQrData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/qr/get`);
        if (response.data.status) {
          const latestData = response.data.data[response.data.data.length - 1];
          setQrImage(latestData.qr_imaage); // Assuming typo in API response
          setApiUpiId(latestData.upi_id);
        }
      } catch (error) {
        console.error("Error fetching QR data:", error);
        setSnackbar({
          open: true,
          message: "Failed to fetch payment details. Using default values.",
          severity: "warning",
        });
      }
    };

    fetchQrData();
  }, []);

  // Close the snackbar notification
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Copy UPI ID to clipboard
  const handleCopyUpi = () => {
    navigator.clipboard.writeText(apiUpiId || "kamaiclub@okaxisbank").then(() => {
      setSnackbar({
        open: true,
        message: "UPI ID copied to clipboard!",
        severity: "success",
      });
    });
  };

  // Handle file upload for payment screenshot
  const handleFileUpload = (event) => {
    setScreenshot(event.target.files[0]);
  };

  // Submit payment details to the server
  const handleSubmit = async () => {
    if (!transactionId || !screenshot || !email) {
      setSnackbar({
        open: true,
        message: "Please provide Transaction ID and upload a screenshot.",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("transaction_id", transactionId);
      formData.append("verified_status", "inProgress");
      formData.append("image", screenshot);

      const encodedEmail = encodeURIComponent(email);
      const response = await axios.post(`${BASE_URL}/api/subscription/${encodedEmail}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200 || response.status === 201) {
        setSnackbar({
          open: true,
          message:
            response.data.message ||
            "Payment details submitted successfully! Please wait for 24 hours.",
          severity: "success",
        });

        setTransactionId("");
        setScreenshot(null);
        window.location.reload();
      }
    } catch (error) {
      console.error("Error submitting payment details:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to submit payment details. Please try again.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isMobile = useMediaQuery("(max-width:600px)");
  const classes = useStyles();

  if (isSubscribed) {
    onClose();
    return null;
  }

  return (
    <>
      <Box className={classes.modalContainer}>
        <Box
          className={classes.popupContent}
          height={isMobile ? "100%" : "80%"}
          width={isMobile ? "100%" : "80%"}
        >
          {verifiedStatus === "pending" ? (
            <>
              <Typography variant={isMobile ? "h4" : "h3"} className={classes.header}>
                Pay the Subscription Fee
              </Typography>
              <Box className={classes.paymentDetails}>
                {/* Left Section: QR Code and UPI Info */}
                <Box
                  className={classes.qrCodeContainer}
                  width={isMobile ? "100%" : "45%"}
                  textAlign="center"
                >
                  <img className={classes.qrCodeImage} src={qrImage} alt="QR Code" />
                  <Box className={isMobile ? classes.paymentMethodsMobile : classes.paymentMethods}>
                    <img
                      className={isMobile ? classes.paymentIconMobile : classes.paymentIcon}
                      src="https://www.fintechfutures.com/files/2023/01/phonepe-logo-icon.jpg"
                      alt="PhonePe"
                    />
                    <img
                      className={isMobile ? classes.paymentIconMobile : classes.paymentIcon}
                      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTYQ3uhlEkLwNJ9Y6kjLphe_irfN1i9H1pCMw&s"
                      alt="Paytm"
                    />
                  </Box>
                  <Box className={isMobile ? classes.upiIdMobile : classes.upiInfoBox}>
                    <Typography color="#4caf50" fontWeight="500" className={classes.upiText}>
                      UPI ID: {apiUpiId}
                    </Typography>
                    <IconButton onClick={handleCopyUpi}>
                      <CopyIcon color="secondary" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Right Section: Payment Details Form */}
                <Box className={classes.formBox} width={isMobile ? "100%" : "45%"}>
                  <Typography variant="h4" mb={2}>
                    Payment Details
                  </Typography>
                  <TextField
                    label="Reference ID"
                    variant="outlined"
                    fullWidth
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    className={classes.input}
                    margin="normal"
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    component="label"
                    className={classes.uploadButton}
                  >
                    <Typography fontSize={14} fontWeight="600" color="rgb(248, 248, 248)">
                      Upload Payment Screenshot
                    </Typography>
                    <input type="file" hidden onChange={handleFileUpload} />
                  </Button>
                  {screenshot && (
                    <Typography className={classes.screenshotText}>
                      Screenshot Uploaded: {screenshot.name}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box mt={2} display="flex" justifyContent={isMobile ? "center" : "flex-end"}>
                <Button
                  style={{ width: isMobile ? "100%" : "45%" }}
                  variant="contained"
                  sx={{
                    background: "linear-gradient(195deg, rgb(121, 121, 121), rgb(25, 25, 25))",
                  }}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  <Typography variant="body2" color="#fff" fontWeight="600">
                    {loading ? "Submitting..." : "Submit Payment Details"}
                  </Typography>
                </Button>
              </Box>
            </>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100vh"
              textAlign="center"
            >
              <Typography variant="h3" gutterBottom>
                Payment Status
              </Typography>
              <img
                src={waiting}
                height="340px"
                width="340px"
                alt="Waiting for payment verification"
              />
              <Typography variant="h4" gutterBottom>
                Your Payment is Under Process
              </Typography>
              <Box style={{ width: isMobile ? "90%" : "80%" }}>
                <Typography variant="body2" color="gray" gutterBottom>
                  Please wait, your payment will be verified within 24 hours. If any issue please
                  contact KamaiClub admin on
                  <span
                    style={{
                      fontSize: isMobile ? "18px" : "22px",
                      color: "#49a3f1",
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                  >
                    info@kamaiclub.com
                  </span>
                  or
                  <span
                    style={{
                      fontSize: isMobile ? "18px" : "22px",
                      color: "#4caf50",
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                  >
                    9876543201
                  </span>
                </Typography>
              </Box>
              <Button
                onClick={() => {
                  window.location.reload();
                }}
                variant="contained"
                style={{ width: "80%", marginTop: "5%" }}
                sx={{
                  background: "linear-gradient(195deg, rgb(121, 121, 121), rgb(25, 25, 25))",
                  color: "#fff",
                  fontWeight: "600",
                }}
              >
                Refresh
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 10000 }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

PaymentPopup.propTypes = {
  isSubscribed: PropTypes.bool.isRequired,
  verifiedStatus: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PaymentPopup;
